import type {
  ServerReservationBaggagesOption,
  ServerReservationBaggagesResult,
} from 'api/flights'

/**
 * The id a baggage option is selected by.
 *
 * Derived rather than provider-supplied, and exported because checkout rebuilds it from a stored
 * selection to reopen the picker pre-filled. A product code repeats across legs, so the leg has to
 * be part of the key.
 */
export function postBookingBaggageOptionId(legIndex: number, productCode: string): string {
  return `${legIndex}:${productCode}`
}

export interface PostBookingBaggageOption {
  /** Unique within the whole response. The same product on two legs must not collapse into one. */
  id: string
  productCode: string
  legIndex: number
  /** NDC only. Echoed back on the addon payload so the provider books against the exact offer. */
  offerItemId?: string
  label: string
  /**
   * What we charge. This is the provider's own `price.amount`, forwarded untouched: svc-flights
   * records the cached option's amount as the addon price and svc-order never compares the two,
   * so any re-derivation here would silently charge one number and record another.
   */
  amount: number
  currency: string
}

export interface PostBookingBaggageLegOptions {
  legIndex: number
  travellers: Array<{ travellerId: string, options: Array<PostBookingBaggageOption> }>
}

export interface PostBookingBaggageAvailability {
  currency: string
  legs: Array<PostBookingBaggageLegOptions>
}

/**
 * Whether a single-select row can honestly sell this option.
 *
 * `WEIGHT_OVERAGE` prices per kilogram over the free allowance and `FARE_PERCENTAGE` per cent of
 * the fare, so neither has a single "one bag costs X" amount. Rendering the unit price as the bag
 * price would understate what the airline charges, which is worse than not offering the bag.
 * An absent `feeBasis` is NDC's way of saying flat.
 */
function isFlatFee(option: ServerReservationBaggagesOption): boolean {
  return option.feeBasis === undefined || option.feeBasis === 'FLAT'
}

/**
 * A baggage allowance as svc-flights reports it, both on an offer and on a purchased addon. The two
 * are the same shape on purpose, which is what lets one bag carry the same weight label from the
 * picker through to the purchased row on the traveller card.
 */
export interface BaggageKiloAllowance {
  weightLimits?: Array<{
    value: number
    unit: 'KG' | 'LB'
    limitType: 'MAX' | 'OVER'
  }>
}

/**
 * The kilogram allowance, or undefined when the airline quoted no kilogram limit.
 *
 * `MAX` wins over `OVER`: an `OVER` limit is the threshold a surcharge starts at, not the weight
 * the bag buys, so labelling it "23kg" would tell the customer they get an allowance they do not.
 * The fallback keeps an airline that quotes only one KG row, with no limitType we recognise, sellable.
 */
export function getKiloWeight(allowance: BaggageKiloAllowance | undefined): number | undefined {
  const kiloLimits = allowance?.weightLimits?.filter(limit => limit.unit === 'KG') ?? []
  const max = kiloLimits.find(limit => limit.limitType === 'MAX')
  return (max ?? kiloLimits[0])?.value
}

export function postBookingBaggageMap(
  result: ServerReservationBaggagesResult,
): PostBookingBaggageAvailability {
  let currency = ''

  const legs = (result.legs ?? []).map((leg) => {
    const travellerOrder: Array<string> = []
    const optionsByTraveller = new Map<string, Array<PostBookingBaggageOption>>()

    for (const ancillary of leg.ancillaries ?? []) {
      if (!ancillary.available) { continue }
      if (!isFlatFee(ancillary)) { continue }

      const kg = getKiloWeight(ancillary.allowance)
      if (kg === undefined) { continue }

      if (!currency) { currency = ancillary.price.currency }

      for (const travellerId of ancillary.travellerIds ?? []) {
        if (!optionsByTraveller.has(travellerId)) {
          optionsByTraveller.set(travellerId, [])
          travellerOrder.push(travellerId)
        }
        optionsByTraveller.get(travellerId)!.push({
          id: postBookingBaggageOptionId(leg.legIndex, ancillary.productCode),
          productCode: ancillary.productCode,
          legIndex: leg.legIndex,
          offerItemId: ancillary.offerItemId,
          label: `${kg}kg`,
          amount: ancillary.price.amount,
          currency: ancillary.price.currency,
        })
      }
    }

    return {
      legIndex: leg.legIndex,
      travellers: travellerOrder.map(travellerId => ({
        travellerId,
        options: optionsByTraveller.get(travellerId)!,
      })),
    }
  })

  return { currency, legs }
}
